import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataSection from './Datasection';
import { useNavigate } from 'react-router-dom';

const CoachingProgramsSection = ({ showNotification }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    tag: 'Offline',
    display_order: 0,
    active_status: true,
    image: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  const columns = [
    { key: 'id', header: 'ID' },
    { key: 'imagePreview', header: 'Image' },
    { key: 'tag', header: 'Tag' },
    { key: 'activeStatus', header: 'Status' },
    { key: 'display_order', header: 'Order' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('https://apissc.sainikschoolcadet.com/api/coaching-programs');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching coaching programs:', error);
      showNotification('Failed to load coaching programs', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      tag: 'Offline',
      display_order: 0,
      active_status: true,
      image: null
    });
    setImagePreview(null);
    setErrors({});
    setSelectedItem(null);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      tag: item.tag || 'Offline',
      display_order: item.display_order || 0,
      active_status: item.active === true || item.active === 1 || item.activeStatus === 'Active',
      image: null
    });
    if (item.imagePreview) {
      setImagePreview(item.imagePreview);
    }
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this program?')) {
      try {
        await axios.delete(`https://apissc.sainikschoolcadet.com/api/coaching-programs/${id}`);
        setData(data.filter(item => item.id !== id));
        showNotification('Program deleted successfully');
      } catch (error) {
        console.error('Error deleting coaching program:', error);
        showNotification('Failed to delete program', 'error');
      }
    }
  };
  
  const handleView = (tag) => {
    // Navigate to program-details with the tag (online/offline) as a parameter
    navigate(`/program-details/${tag.toLowerCase()}`);
  };

  const handleAdd = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      const file = files[0];
      if (file) {
        setFormData({ ...formData, [name]: file });
        
        // Create preview for the selected image
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    } else if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (name === 'display_order') {
      setFormData({ ...formData, [name]: parseInt(value, 10) || 0 });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!selectedItem && !formData.image) {
      newErrors.image = 'Please select an image';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setFormLoading(true);
    
    try {
      let imagePath = null;
      
      // First, handle image upload if there's a new image
      if (formData.image) {
        const imageFormData = new FormData();
        imageFormData.append('image', formData.image);
        imageFormData.append('title', `Coaching Program ${formData.tag ? '- ' + formData.tag.substring(0, 30) : ''}`);
        imageFormData.append('tag', 'coaching-program');
        
        try {
          const imageUploadResponse = await axios.post(
            'https://apissc.sainikschoolcadet.com/api/images',
            imageFormData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          
          if (imageUploadResponse.data && imageUploadResponse.data.path) {
            imagePath = imageUploadResponse.data.path;
          } else {
            throw new Error('Image upload response did not contain a path');
          }
        } catch (imageError) {
          console.error('Error uploading image:', imageError);
          setErrors({ ...errors, image: 'Failed to upload image. Please try again.' });
          setFormLoading(false);
          return;
        }
      }
      
      // Now create/update the coaching program with the image path
      const submitData = {
        tag: formData.tag,
        display_order: formData.display_order,
        active: formData.active_status ? true : false
      };
      
      // Add image path if we have one from upload or keep existing if editing
      if (imagePath) {
        submitData.image_path = imagePath;
      }
      
      let response;
      
      if (selectedItem) {
        // Update existing item
        response = await axios.put(
          `https://apissc.sainikschoolcadet.com/api/coaching-programs/${selectedItem.id}`, 
          submitData
        );
      } else {
        // Create new item
        response = await axios.post(
          'https://apissc.sainikschoolcadet.com/api/coaching-programs',
          submitData
        );
      }
      
      if (response.data) {
        showNotification(selectedItem ? 'Program updated successfully' : 'Program added successfully');
        setIsModalOpen(false);
        fetchData();
      }
    } catch (error) {
      console.error('Error saving coaching program:', error);
      if (error.response && error.response.data && error.response.data.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: 'Failed to save. Please try again.' });
      }
      showNotification('Failed to save program', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  // Modal component
  const FormModal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
          {children}
        </div>
      </div>
    );
  };

  return (
    <>
      <DataSection
        title="Coaching Programs"
        data={data}
        columns={columns}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        onView={handleView}
        isLoading={isLoading}
        emptyMessage="No coaching programs found"
      />
      
      <FormModal isOpen={isModalOpen} onClose={closeModal}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <h2 className="text-xl font-bold mb-4">
            {selectedItem ? 'Edit Coaching Program' : 'Add New Coaching Program'}
          </h2>
          
          <div className="form-group">
            <label htmlFor="tag" className="block mb-1 font-medium">
              Tag
            </label>
            <select
              id="tag"
              name="tag"
              value={formData.tag}
              onChange={handleChange}
              className="w-full p-2 border rounded border-gray-300"
            >
              <option value="Offline">Offline</option>
              <option value="Online">Online</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="display_order" className="block mb-1 font-medium">
              Display Order
            </label>
            <input
              type="number"
              id="display_order"
              name="display_order"
              value={formData.display_order}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.display_order ? 'border-red-500' : 'border-gray-300'}`}
              min="0"
            />
            {errors.display_order && (
              <p className="text-red-500 text-sm mt-1">{errors.display_order}</p>
            )}
          </div>
          
          <div className="form-group">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                name="active_status"
                checked={formData.active_status}
                onChange={handleChange}
                className="form-checkbox h-5 w-5"
              />
              <span>Active</span>
            </label>
          </div>
          
          <div className="form-group">
            <label htmlFor="image" className="block mb-1 font-medium">
              Image {!selectedItem && '*'}
            </label>
            <input
              type="file"
              id="image"
              name="image"
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.image ? 'border-red-500' : 'border-gray-300'}`}
              accept="image/*"
            />
            {errors.image && (
              <p className="text-red-500 text-sm mt-1">{errors.image}</p>
            )}
            
            {imagePreview && (
              <div className="mt-2">
                <p className="text-sm font-medium mb-1">Preview:</p>
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-40 h-auto object-cover border rounded"
                />
              </div>
            )}
          </div>
          
          {errors.general && (
            <div className="bg-red-100 text-red-700 p-3 rounded">
              {errors.general}
            </div>
          )}
          
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              disabled={formLoading}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={formLoading}
            >
              {formLoading ? (
                <span>Saving...</span>
              ) : (
                <span>{selectedItem ? 'Update' : 'Save'}</span>
              )}
            </button>
          </div>
        </form>
      </FormModal>
    </>
  );
};

export default CoachingProgramsSection;